import React, { useMemo } from 'react'
import { Header } from '../../components/marketing/Header'
import { FeatureAnnouncementBanner } from '../../components/marketing/FeatureAnnouncementBanner'
import Meta from '../../components/Meta'
import Section from 'components/common/Section'
import { FeatureHero } from 'components/marketing/FeatureHero'
import { contentful } from 'utils/contentful'
import { SectionHeader } from 'components/marketing/SectionHeader'
import CaseStudyGrid from 'components/marketing/CaseStudyGrid'
import {
  CaseStudy,
  CaseStudyGroup,
  PageQuote,
} from 'types/generated/contentful'
import { GetStaticPaths, GetStaticProps } from 'next'
import IconRenderer, { Icon } from 'components/marketing/IconRenderer'
import RichTextRenderer from 'components/marketing/RichTextRenderer'
import CaseStudyLogoWall from 'components/marketing/CaseStudyLogoWall'
import { Button } from '../../designsystem/Button'

type Props = {
  caseStudyGroup: CaseStudyGroup
  caseStudies: CaseStudy[]
  pageQuotes: PageQuote[]
}

export default function CaseStudiesIndex({
  caseStudyGroup,
  caseStudies,
  pageQuotes,
}: Props): JSX.Element | null {
  const {
    title,
    headline,
    slug,
    metaImage,
    metaDescription,
    subline,
    body,
    type,
    heroCtaLabel,
    heroCtaLink,
    bottomCtaLabel,
    bottomCtaLink,
  } = caseStudyGroup

  // Calculate the average improvement/uptime percentages
  const increasedPerformances: number[] = []
  const serverLoadReductions: number[] = []

  if (caseStudies.length > 0) {
    caseStudies.map((useCase) => {
      if (
        useCase.improvementType === 'increased-performance' &&
        useCase.increasedPerformance
      ) {
        increasedPerformances.push(useCase.increasedPerformance)
      } else if (
        useCase.improvementType === 'server-load-reduction' &&
        useCase.serverLoadReduction
      ) {
        serverLoadReductions.push(useCase.serverLoadReduction)
      }
    })
  }

  const showLogoWall = useMemo(
    () => caseStudies && caseStudies.length > 2,
    [caseStudies],
  )

  const pageTitle = useMemo(
    () =>
      headline ||
      (slug === 'other'
        ? 'GraphQL Edge Caching Success Stories'
        : title
          ? `GraphQL Edge Caching for ${title}`
          : 'Use Cases'),
    [headline, slug, title],
  )

  const caseStudiesAndPageQuotes = useMemo(
    () => [
      // @todo hackish way to sort Dylan's quotes to the top
      ...pageQuotes.sort((a, b) => {
        if (a.quote?.author?.name === 'Dylan Valade') return -1
        if (b.quote?.author?.name === 'Dylan Valade') return 1
        return 0
      }),
      ...caseStudies,
    ],
    [caseStudies, pageQuotes],
  )

  return (
    <div className='bg-grey'>
      <Meta
        title={pageTitle}
        description={metaDescription}
        imageSrc={metaImage?.url}
        imageHeight={metaImage?.height}
        imageWidth={metaImage?.width}
      />
      <FeatureAnnouncementBanner />
      <Header />

      <main className='marketing-page'>
        <Section verticalPadding='first'>
          <FeatureHero
            icon={
              <IconRenderer
                icon={slug as Icon}
                className={type === 'industry' ? 'text-red' : 'text-magenta'}
              />
            }
            headline={pageTitle}
            subline={subline || ''}
            theme='blue'
          />
          {/* Show logos if we have more than 2 case studies */}
          {showLogoWall && (
            <CaseStudyLogoWall
              label='CASE STUDIES:'
              className='spacer-lg'
              caseStudies={caseStudies}
              maxDisplayedCaseStudies={6} // Limiting to 6 case studies for now
            />
          )}
          {heroCtaLabel && heroCtaLink && (
            <div className='flex justify-center items-center spacer-md'>
              <a href={heroCtaLink} target='_blank' rel='noreferrer'>
                <Button>{heroCtaLabel}</Button>
              </a>
            </div>
          )}
        </Section>

        {body && (
          <Section bgColor='white'>
            <RichTextRenderer data={body} />
          </Section>
        )}

        <Section noTopPadding={!body} noBottomPadding>
          {!showLogoWall && !!body && (
            <SectionHeader
              paddingToContent='small'
              title='Don’t just take our word'
              label={
                caseStudies && caseStudies.length > 1
                  ? 'Case studies'
                  : 'Case study'
              }
            />
          )}

          <CaseStudyGrid caseStudiesAndPageQuotes={caseStudiesAndPageQuotes} />

          <div className='flex justify-center items-center py-12 md:py-16 lg:py-24'>
            {bottomCtaLabel && bottomCtaLink && (
              <a href={bottomCtaLink} target='_blank' rel='noreferrer'>
                <Button>{bottomCtaLabel}</Button>
              </a>
            )}
          </div>
        </Section>
      </main>
    </div>
  )
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  try {
    if (params?.slug?.length !== 2) {
      return {
        notFound: true,
      }
    }

    const json = await contentful({
      query: /* GraphQL */ `
        fragment AssetFragment on Asset {
          url
          width
          height
          title
          __typename
        }

        query getCaseStudies($type: String!, $slug: String!) {
        pageQuoteCollection(where: {${params?.slug[0]}: {type: $type, slug: $slug}}, order: sys_publishedAt_DESC) {
            __typename
          items {
            __typename
            sys {
              __typename
              id
            }
            externalLink
            internalLink {
              __typename
              ... on CaseStudy {
                __typename
                slug
              }
              ... on BlogPost {
                __typename
                slug
              }
              ... on CaseStudyGroup {
                __typename
                slug
              }
            }
            title
            quote {
              __typename
              author {
                __typename
                name
                role
                avatar {
                  ...AssetFragment
                }
                company {
                  __typename
                  name
                }
              }
              text
            }
          }
        }
        
        caseStudyGroupCollection(where: {type: $type, slug: $slug}, limit: 1) {
            __typename
          items {
            __typename
            title
            headline
            slug
            metaImage {
              ...AssetFragment
            }
            metaDescription
            subline
            type
            heroCtaLabel
            heroCtaLink
            bottomCtaLabel
            bottomCtaLink
            body {
              __typename
              json
              links {
                __typename
                entries {
                  __typename
                  block {
                    __typename
                    sys {
                      __typename
                      id
                    }
                    ... on CodeBlock {
                      __typename
                      code
                      language
                    }
                    ... on HighlightedLink {
                      __typename
                      type
                      title
                      subline
                      buttonText
                      buttonUrl
                    }
                    ... on AlternatingLayout {
                      __typename
                      label
                      title
                      blocksCollection {
                      __typename
                        items {
                        __typename
                          ... on ComponentVideo {
                            __typename
                            url
                          }
                          ... on AlternatingLayoutItem {
                            __typename
                            sys {
                              __typename
                              id
                            }
                            title
                            text {
                              __typename
                              json
                              links {
                                __typename
                                entries {
                                  __typename
                                  block {
                                    __typename
                                    sys {
                                      __typename
                                      id
                                    }
                                  }
                                }
                                assets {
                                  __typename
                                  hyperlink {
                                    ...AssetFragment
                                    sys {
                                      __typename
                                      id
                                    }
                                  }
                                  block {
                                    ...AssetFragment
                                    sys {
                                      __typename
                                      id
                                    }
                                  }
                                }
                              }
                            }
                            image {
                              ...AssetFragment
                            }
                          }
                        }
                      }
                    }
                    ... on TextWithImages {
                      __typename
                      text {
                        __typename
                        json
                        links {
                          __typename
                          entries {
                            __typename
                            block {
                              __typename
                              sys {
                                __typename
                                id
                              }
                              ... on CodeBlock {
                                __typename
                                code
                                language
                              }
                            }
                          }
                          assets {
                            __typename
                            hyperlink {
                              ...AssetFragment
                              sys {
                                __typename
                                id
                              }
                            }
                            block {
                              ...AssetFragment
                              sys {
                                __typename
                                id
                              }
                            }
                          }
                        }
                      }
                      imagesCollection {
                        __typename
                        items {
                          ...AssetFragment
                        }
                      }
                    }
                  }
                }
                assets {
                  __typename
                  hyperlink {
                    ...AssetFragment
                    sys {
                      __typename
                      id
                    }
                  }
                  block {
                    ...AssetFragment
                    sys {
                      __typename
                      id
                    }
                  }
                }
              }
            }
          }
        }
        caseStudyCollection(where: {${params?.slug[0]}: {type: $type, slug: $slug}}, order: sys_publishedAt_DESC) {
          items {
            __typename
            sys {
              __typename
              id
            }
            slug
            quote {
              __typename
              text
              author {
                __typename
                name
                role
                company {
                  __typename
                  name
                  logo {
                    ...AssetFragment
                  }
                }
                caseStudyImage {
                  ...AssetFragment
                }
              }
            }
            improvementType
            increasedPerformance
            serverLoadReduction
            costReduction
            cardStyle
          }
        }
      }`,
      variables: {
        type: params?.slug[0],
        slug: params?.slug[1],
      },
    })

    if (json.errors && Array.isArray(json.errors) && json.errors.length > 0) {
      throw new Error(
        `Failed to fetch use case page: ${json.errors[0].message}`,
      )
    }

    const caseStudyGroup = json?.data?.caseStudyGroupCollection?.items

    if (caseStudyGroup.length === 0) {
      return {
        notFound: true,
      }
    }

    return {
      props: {
        caseStudyGroup: caseStudyGroup[0],
        caseStudies: json?.data?.caseStudyCollection?.items,
        pageQuotes: json?.data?.pageQuoteCollection?.items ?? [],
      },
      revalidate: 10, // In seconds
    }
  } catch (e) {
    console.log(e)
    throw new Error('Failed to fetch case use case page')
  }
}

export const getStaticPaths: GetStaticPaths = async () => {
  const json = await contentful({
    query: `query getCaseStudyPaths {
      caseStudyGroupCollection {
        items {
          slug
          type
        }
      }
    }`,
  })

  const paths = json.data.caseStudyGroupCollection.items.map((caseStudy) => ({
    params: { slug: [caseStudy.type, caseStudy.slug] },
  }))

  return { paths, fallback: 'blocking' }
}
